export type MarkdownFormat =
  | 'heading-1'
  | 'heading-2'
  | 'heading-3'
  | 'heading-4'
  | 'bold'
  | 'italic'
  | 'link'
  | 'bulleted-list'
  | 'numbered-list'
  | 'quote'
  | 'inline-code'
  | 'table';

export interface MarkdownFormattingResult {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

interface Selection {
  start: number;
  end: number;
}

const normaliseSelection = (
  value: string,
  selectionStart: number,
  selectionEnd: number
): Selection => {
  const start = Math.max(0, Math.min(selectionStart, value.length));
  const end = Math.max(0, Math.min(selectionEnd, value.length));

  return start <= end ? { start, end } : { start: end, end: start };
};

const wrapSelection = (
  value: string,
  selection: Selection,
  opening: string,
  closing: string,
  placeholder: string
): MarkdownFormattingResult => {
  const selectedText = value.slice(selection.start, selection.end);
  const leadingWhitespace = selectedText.match(/^\s*/)?.[0] || '';
  const trailingWhitespace = selectedText.match(/\s*$/)?.[0] || '';
  const selectedContent = selectedText
    ? selectedText.slice(
        leadingWhitespace.length,
        selectedText.length - trailingWhitespace.length
      )
    : '';
  const content = selectedContent || placeholder;
  const preservedLeadingWhitespace = selectedContent
    ? leadingWhitespace
    : selectedText;
  const preservedTrailingWhitespace = selectedContent ? trailingWhitespace : '';
  const replacement = `${preservedLeadingWhitespace}${opening}${content}${closing}${preservedTrailingWhitespace}`;
  const contentStart =
    selection.start + preservedLeadingWhitespace.length + opening.length;

  return {
    value:
      value.slice(0, selection.start) +
      replacement +
      value.slice(selection.end),
    selectionStart: contentStart,
    selectionEnd: contentStart + content.length,
  };
};

const wrapCodeSelection = (
  value: string,
  selection: Selection
): MarkdownFormattingResult => {
  const selectedText = value.slice(selection.start, selection.end);

  if (!selectedText) {
    return wrapSelection(value, selection, '`', '`', 'code');
  }

  const backtickRuns: string[] = selectedText.match(/`+/g) || [];
  const longestBacktickRun = backtickRuns.reduce<number>(
    (longest, run) => Math.max(longest, run.length),
    0
  );
  const delimiter = '`'.repeat(longestBacktickRun + 1);
  const padding = longestBacktickRun ? ' ' : '';

  return wrapSelection(
    value,
    selection,
    `${delimiter}${padding}`,
    `${padding}${delimiter}`,
    'code'
  );
};

const insertLink = (
  value: string,
  selection: Selection
): MarkdownFormattingResult => {
  const selectedText = value.slice(selection.start, selection.end);
  const linkText = selectedText
    ? selectedText.replace(/([\\[\]])/g, '\\$1')
    : 'link text';
  const destination = 'https://';
  const replacement = `[${linkText}](${destination})`;

  if (!selectedText) {
    return {
      value:
        value.slice(0, selection.start) +
        replacement +
        value.slice(selection.end),
      selectionStart: selection.start + 1,
      selectionEnd: selection.start + 1 + linkText.length,
    };
  }

  const destinationStart = selection.start + linkText.length + 3;

  return {
    value:
      value.slice(0, selection.start) +
      replacement +
      value.slice(selection.end),
    selectionStart: destinationStart,
    selectionEnd: destinationStart + destination.length,
  };
};

const getSelectedLineRange = (
  value: string,
  selection: Selection
): Selection => {
  const start = value.lastIndexOf('\n', selection.start - 1) + 1;
  const endSearchFrom =
    selection.end > selection.start && value[selection.end - 1] === '\n'
      ? selection.end - 1
      : selection.end;
  const nextNewLine = value.indexOf('\n', endSearchFrom);

  return {
    start,
    end: nextNewLine === -1 ? value.length : nextNewLine,
  };
};

const prefixSelectedLines = (
  value: string,
  selection: Selection,
  prefixForLine: (lineIndex: number) => string
): MarkdownFormattingResult => {
  const lineRange = getSelectedLineRange(value, selection);
  const selectedLines = value.slice(lineRange.start, lineRange.end).split('\n');
  const prefixes = selectedLines.map((_, index) => prefixForLine(index));
  const replacement = selectedLines
    .map((line, index) => `${prefixes[index]}${line}`)
    .join('\n');
  const nextValue =
    value.slice(0, lineRange.start) + replacement + value.slice(lineRange.end);

  if (selection.start === selection.end) {
    const lineIndex =
      value.slice(lineRange.start, selection.start).split('\n').length - 1;
    const prefixLengthBeforeCursor = prefixes
      .slice(0, lineIndex + 1)
      .reduce((total, prefix) => total + prefix.length, 0);

    return {
      value: nextValue,
      selectionStart: selection.start + prefixLengthBeforeCursor,
      selectionEnd: selection.end + prefixLengthBeforeCursor,
    };
  }

  return {
    value: nextValue,
    selectionStart: lineRange.start,
    selectionEnd: lineRange.start + replacement.length,
  };
};

const setHeadingLevel = (
  value: string,
  selection: Selection,
  level: 1 | 2 | 3 | 4
): MarkdownFormattingResult => {
  const lineRange = getSelectedLineRange(value, selection);
  const selectedLines = value.slice(lineRange.start, lineRange.end).split('\n');
  const headingPrefix = `${'#'.repeat(level)} `;
  const existingPrefixes = selectedLines.map(
    (line) => line.match(/^#{1,6}[ \t]+/)?.[0] || ''
  );
  const replacement = selectedLines
    .map(
      (line, index) =>
        `${headingPrefix}${line.slice(existingPrefixes[index].length)}`
    )
    .join('\n');
  const nextValue =
    value.slice(0, lineRange.start) + replacement + value.slice(lineRange.end);

  if (selection.start === selection.end) {
    const oldPrefixLength = existingPrefixes[0].length;
    const cursorWithinLine = selection.start - lineRange.start;
    const cursorWithinContent = Math.max(0, cursorWithinLine - oldPrefixLength);
    const nextCursor =
      lineRange.start + headingPrefix.length + cursorWithinContent;

    return {
      value: nextValue,
      selectionStart: nextCursor,
      selectionEnd: nextCursor,
    };
  }

  return {
    value: nextValue,
    selectionStart: lineRange.start,
    selectionEnd: lineRange.start + replacement.length,
  };
};

const tableCellContent = (selectedText: string) => {
  const content = selectedText.trim() || 'value';

  return content
    .replace(/\\/g, '\\\\')
    .replace(/\|/g, '\\|')
    .replace(/\s*\r?\n\s*/g, ' ');
};

const leadingBlockBreak = (value: string, position: number) => {
  if (position === 0 || value.slice(0, position).endsWith('\n\n')) {
    return '';
  }

  return value[position - 1] === '\n' ? '\n' : '\n\n';
};

const trailingBlockBreak = (value: string, position: number) => {
  if (position === value.length || value.slice(position).startsWith('\n\n')) {
    return '';
  }

  return value[position] === '\n' ? '\n' : '\n\n';
};

const insertTable = (
  value: string,
  selection: Selection
): MarkdownFormattingResult => {
  const firstCell = tableCellContent(
    value.slice(selection.start, selection.end)
  );
  const tablePrefix = '| Column 1 | Column 2 |\n| --- | --- |\n| ';
  const table = `${tablePrefix}${firstCell} | value |`;
  const leadingBreak = leadingBlockBreak(value, selection.start);
  const trailingBreak = trailingBlockBreak(value, selection.end);
  const replacement = `${leadingBreak}${table}${trailingBreak}`;
  const firstCellStart =
    selection.start + leadingBreak.length + tablePrefix.length;

  return {
    value:
      value.slice(0, selection.start) +
      replacement +
      value.slice(selection.end),
    selectionStart: firstCellStart,
    selectionEnd: firstCellStart + firstCell.length,
  };
};

export const applyMarkdownFormatting = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  format: MarkdownFormat
): MarkdownFormattingResult => {
  const selection = normaliseSelection(value, selectionStart, selectionEnd);

  switch (format) {
    case 'bold':
      return wrapSelection(value, selection, '**', '**', 'bold text');
    case 'italic':
      return wrapSelection(value, selection, '_', '_', 'italic text');
    case 'inline-code':
      return wrapCodeSelection(value, selection);
    case 'link':
      return insertLink(value, selection);
    case 'heading-1':
      return setHeadingLevel(value, selection, 1);
    case 'heading-2':
      return setHeadingLevel(value, selection, 2);
    case 'heading-3':
      return setHeadingLevel(value, selection, 3);
    case 'heading-4':
      return setHeadingLevel(value, selection, 4);
    case 'bulleted-list':
      return prefixSelectedLines(value, selection, () => '- ');
    case 'numbered-list':
      return prefixSelectedLines(value, selection, (index) => `${index + 1}. `);
    case 'quote':
      return prefixSelectedLines(value, selection, () => '> ');
    case 'table':
      return insertTable(value, selection);
  }
};
